// src/lib/settingsNav.js
//
// Las secciones de configuración, en un solo sitio. Es el hermano de
// `lib/navigation.js` y existe por el mismo motivo: la tabla la consumen el
// sub-nav del shell y el ruteo, y tenerla partida en dos era lo que hacía que
// añadir una sección fueran dos ediciones que se podían desincronizar.
//
// El eje aquí es el **alcance**, no el objeto: cada sección pertenece a uno de
// los tres niveles de la jerarquía y ningún nivel administra lo del otro.
//
//   usuario        te sigue cambies de contexto o no
//   organización   posee cuentas y miembros
//   cuenta         posee los recursos y el dinero
//
// La prueba para clasificar una sección nueva: ¿esto me sigue si cambio de
// contexto? Si sí, es del usuario. Si no, ¿es propiedad de la organización o de
// una de sus cuentas?
import {
  CircleUserRound,
  ShieldCheck,
  SlidersHorizontal,
  Building2,
  Users,
  CreditCard,
  Settings as SettingsIcon,
  IdCard,
  BarChart3,
  FolderOpen,
  Wallet,
} from "lucide-react";

export const SCOPES = {
  USER: "user",
  ORG: "org",
  ACCOUNT: "account",
};

// `manageOnly` marca lo que solo ve quien administra. No es solo permisos: el
// menú no debe ofrecer lo que no se puede usar.
// El orden dentro de cada alcance no es el de declaración: primero lo que
// identifica al objeto, luego quién está en él, luego lo que contiene, y el
// dinero al final.
export const SETTINGS_SECTIONS = [
  // ── Usuario ────────────────────────────────────────────────────────────────
  {
    id: "profile",
    scope: SCOPES.USER,
    label: "Profile",
    i18nKey: "settingsNav.profile",
    Icon: CircleUserRound,
  },
  {
    id: "security",
    scope: SCOPES.USER,
    label: "Security",
    i18nKey: "settingsNav.security",
    Icon: ShieldCheck,
  },
  {
    id: "preferences",
    scope: SCOPES.USER,
    label: "Preferences",
    i18nKey: "settingsNav.preferences",
    Icon: SlidersHorizontal,
  },

  // ── Organización ───────────────────────────────────────────────────────────
  {
    id: "overview",
    scope: SCOPES.ORG,
    label: "Overview",
    i18nKey: "settingsNav.overview",
    Icon: Building2,
  },
  {
    id: "settings",
    scope: SCOPES.ORG,
    label: "Settings",
    i18nKey: "settingsNav.settings",
    Icon: SettingsIcon,
    manageOnly: true,
  },
  {
    id: "members",
    scope: SCOPES.ORG,
    label: "Members",
    i18nKey: "settingsNav.members",
    Icon: Users,
    manageOnly: true,
  },
  {
    // Las cuentas que la organización posee. Es donde se crean: crear una cuenta
    // no es una operación *de* una cuenta, es de la organización que la va a
    // contener.
    id: "accounts",
    scope: SCOPES.ORG,
    label: "Accounts",
    i18nKey: "settingsNav.accounts",
    Icon: Wallet,
    manageOnly: true,
  },
  {
    // El pool consolidado, la suscripción enterprise, el consumo por cuenta y
    // las cuotas. Se llama por lo que contiene y no "Billing" porque la cuenta
    // ya tiene una sección con ese nombre.
    id: "usage",
    scope: SCOPES.ORG,
    label: "Usage & quotas",
    i18nKey: "settingsNav.orgUsage",
    Icon: BarChart3,
    manageOnly: true,
  },

  // ── Cuenta ─────────────────────────────────────────────────────────────────
  {
    id: "general",
    scope: SCOPES.ACCOUNT,
    label: "General",
    i18nKey: "settingsNav.general",
    // Compartía icono con Preferencias del usuario, así que a simple vista
    // parecían la misma entrada repetida en dos grupos.
    Icon: SettingsIcon,
  },
  {
    // Administrar proyectos —crear, renombrar, borrar— es de la cuenta, que es
    // quien los posee: en la base de datos un proyecto lleva `account_id` u
    // `organization_id`, nunca `user_id`.
    //
    // No es un segundo hogar del objeto: el sitio donde se *trabaja* en un
    // proyecto es su espacio de trabajo, y desde aquí se abre. Lo que Profile
    // pintaba antes era una lista que solo miraba.
    id: "projects",
    scope: SCOPES.ACCOUNT,
    label: "Projects",
    i18nKey: "settingsNav.projects",
    Icon: FolderOpen,
  },
  {
    id: "billing",
    scope: SCOPES.ACCOUNT,
    label: "Billing",
    i18nKey: "settingsNav.billing",
    Icon: CreditCard,
  },
  {
    id: "credentials",
    scope: SCOPES.ACCOUNT,
    label: "Credentials",
    i18nKey: "settingsNav.credentials",
    Icon: IdCard,
  },
];

/** Secciones de un alcance, ya filtradas por lo que el rol permite ver. */
export function sectionsForScope(scope, { canManage = true } = {}) {
  return SETTINGS_SECTIONS.filter(
    (s) => s.scope === scope && (canManage || !s.manageOnly)
  );
}

/** La sección a la que se aterriza cuando la URL no nombra ninguna. */
export function defaultSectionFor(scope, options) {
  return sectionsForScope(scope, options)[0]?.id || null;
}

export function isSettingsSection(scope, id) {
  return SETTINGS_SECTIONS.some((s) => s.scope === scope && s.id === id);
}

/**
 * Ruta de una sección. El alcance va en la URL —y no en estado— para que
 * compartir un enlace, recargar y tener dos pestañas abiertas en contextos
 * distintos funcionen. Con el alcance en memoria, ninguna de las tres funciona.
 */
export function settingsPath(scope, sectionId, { orgId, accountId } = {}) {
  switch (scope) {
    case SCOPES.USER:
      return `/u/settings/${sectionId}`;
    // `/o/` y `/a/` y no `/organizations/` ni `/accounts/`: el proxy de
    // desarrollo manda esos dos prefijos enteros al backend (`setupProxy.js`,
    // `pathFilter: '/accounts/**'` y `'/organizations/**'`), así que navegar por
    // la app funcionaba pero recargar o abrir el enlace directo devolvía el JSON
    // de error del API en vez de la pantalla.
    case SCOPES.ORG:
      return orgId ? `/o/${orgId}/${sectionId}` : null;
    case SCOPES.ACCOUNT:
      return accountId ? `/a/${accountId}/${sectionId}` : null;
    default:
      return null;
  }
}

/**
 * Los tres alcances como grupos de un mismo sub-nav.
 *
 * Cada alcance conserva su familia de rutas —la URL sigue diciendo la verdad
 * sobre de quién es lo que estás mirando—, pero la navegación los muestra
 * juntos. Es lo que hacen Linear y Notion, y aquí es la única opción que
 * funciona: el modelo de GitHub —entrar a la configuración de cada objeto desde
 * su propia página— necesita que ese objeto *tenga* página, y en Zenit la cuenta
 * no la tiene. Separarlos dejó el alcance de cuenta sin ninguna puerta.
 *
 * Escala mientras la API exponga una cuenta por organización
 * (`organizational_account_id`, en singular). Si algún día expone varias, esto
 * pasa a ser una lista y el switcher gana un nivel.
 */
export function settingsGroups({
  orgId,
  orgName,
  accountId,
  accountName,
  canManage = true,
  t = (_key, fallback) => fallback,
} = {}) {
  const groups = [
    {
      key: SCOPES.USER,
      // "Tú" y no "Mi cuenta": el punto de todo esto es que lo de la persona y
      // lo de la cuenta son cosas distintas, y llamar "cuenta" a las dos era
      // exactamente el problema.
      label: t("settingsNav.userScope", "You"),
      sections: sectionsForScope(SCOPES.USER).map((section) => ({
        ...section,
        path: settingsPath(SCOPES.USER, section.id),
      })),
    },
  ];

  const accountSections = accountId
    ? sectionsForScope(SCOPES.ACCOUNT).map((section) => ({
        ...section,
        path: settingsPath(SCOPES.ACCOUNT, section.id, { accountId }),
      }))
    : [];

  if (accountSections.length) {
    groups.push({
      key: SCOPES.ACCOUNT,
      label: accountName || t("settingsNav.accountScope", "Account"),
      sections: accountSections,
    });
  }

  return groups;
}

/**
 * El grupo de una organización, para su propia pantalla.
 *
 * No sale de `settingsGroups` a propósito. Configuración muestra **la sesión**:
 * tú y la cuenta en la que estás ahora, que no se eligen desde ese menú sino
 * desde el switcher. Una organización no funciona así — se entra a cualquiera de
 * las que perteneces desde la lista, y la que miras no tiene por qué ser la de
 * tu contexto. Mezclar los dos modos de direccionamiento en un mismo menú es lo
 * que lo hacía confuso: dos grupos que dicen "ahora" y uno que dice "cualquiera".
 *
 * El menú es el mismo, porque el menú nunca fue el problema: lo era el modal sin
 * dirección que la organización tenía antes.
 */
export function organizationGroups({ orgId, orgName, canManage = true, t = (_k, f) => f } = {}) {
  if (!orgId) return [];
  const sections = sectionsForScope(SCOPES.ORG, { canManage });
  if (!sections.length) return [];
  return [
    {
      key: SCOPES.ORG,
      label: orgName || t("settingsNav.orgScope", "Organization"),
      sections: sections.map((section) => ({
        ...section,
        path: settingsPath(SCOPES.ORG, section.id, { orgId }),
      })),
    },
  ];
}
